// The four kinds of repair shop Store OS sets up for. Every plan has all four. A business picks one
// at signup; it decides the words on the screens (repair ticket or work order), what is checked
// in (a phone, a computer, a vehicle or a vacuum), the starting categories and service menu, and
// the starting policy text the owner reviews during setup.

export type IndustryId = 'cellphone' | 'computer' | 'auto' | 'vacuum';

export interface Industry {
  id: IndustryId;
  name: string;
  /** What a job is called: "repair ticket" or "work order". */
  job: string;
  /** What's brought in, for headings ("phone", "computer", "vehicle", "vacuum"). */
  item: string;
  /** Starting service menu ("Name | price" per line), changed in Settings. */
  service_menu: string;
  /** Starting repair policy printed on tickets, reviewed during setup. */
  repair_terms: string;
  /** Extra inventory categories this kind of shop starts with (id, name, parent, tracking). */
  categories: { id: string; name: string; parent?: string; tracking: 'serial' | 'quantity' }[];
}

const BACKUP = 'Please back up your device before any repair. We are not responsible for lost data, except as required by law.';
const APPROVAL = 'Prices are confirmed with you before work begins. No extra charges without your approval.';
const PICKUP = 'Please pick up within 30 days of our ready-for-pickup message.';

export const INDUSTRIES: Record<IndustryId, Industry> = {
  cellphone: {
    id: 'cellphone', name: 'Cell phone repair', job: 'repair ticket', item: 'phone',
    service_menu: 'Factory reset | 29.99\nSoftware restore / update | 49.99\nData transfer | 39.99\nNew phone setup | 29.99',
    repair_terms: [BACKUP, APPROVAL, 'Warranty covers the parts we supply and our workmanship. It does not cover accidental or liquid damage.', PICKUP].join('\n'),
    categories: [],
  },
  computer: {
    id: 'computer', name: 'Computer repair', job: 'repair ticket', item: 'computer',
    service_menu: 'Diagnostic | 49.99\nOperating system reinstall | 89.99\nVirus and malware removal | 79.99\nData transfer | 69.99\nNew computer setup | 59.99',
    repair_terms: [BACKUP, APPROVAL, 'Warranty covers the parts we supply and our workmanship. It does not cover accidental or liquid damage, or software you install later.', PICKUP].join('\n'),
    categories: [
      { id: 'laptops', name: 'Laptops', tracking: 'serial' },
      { id: 'desktops', name: 'Desktops', tracking: 'serial' },
      { id: 'computer_parts', name: 'Computer Parts', parent: 'parts', tracking: 'quantity' },
    ],
  },
  auto: {
    id: 'auto', name: 'Auto repair (mechanic)', job: 'work order', item: 'vehicle',
    service_menu: 'Diagnostic (1 hour labor) | 120.00\nOil change, conventional | 49.99\nOil change, synthetic | 79.99\nTire rotation | 29.99\nBrake inspection | 0.00',
    repair_terms: [
      'A written estimate is given before work begins. Work is done only after you authorize it, and any additional work needs your approval first.',
      'Replaced parts are available for you to inspect or keep, unless they must be returned under warranty or as a core.',
      'Warranty covers the parts we supply and our workmanship, as stated on your invoice.',
      'Vehicles left more than 30 days after we tell you the work is done may be subject to storage charges as allowed by law.',
    ].join('\n'),
    categories: [
      { id: 'auto_parts', name: 'Auto Parts', tracking: 'quantity' },
      { id: 'auto_brakes', name: 'Brakes', parent: 'auto_parts', tracking: 'quantity' },
      { id: 'auto_filters', name: 'Filters', parent: 'auto_parts', tracking: 'quantity' },
      { id: 'auto_fluids', name: 'Oil and Fluids', parent: 'auto_parts', tracking: 'quantity' },
      { id: 'auto_electrical', name: 'Batteries and Electrical', parent: 'auto_parts', tracking: 'quantity' },
      { id: 'auto_tires', name: 'Tires', parent: 'auto_parts', tracking: 'quantity' },
      { id: 'auto_other', name: 'Other Auto Parts', parent: 'auto_parts', tracking: 'quantity' },
    ],
  },
  vacuum: {
    id: 'vacuum', name: 'Vacuum repair', job: 'repair ticket', item: 'vacuum',
    service_menu: 'Diagnostic | 25.00\nCleaning and tune-up | 49.99\nBelt replacement (labor) | 19.99\nBrush roll replacement (labor) | 24.99',
    repair_terms: [APPROVAL, 'Warranty covers the parts we supply and our workmanship. It does not cover damage from misuse or liquids.', PICKUP].join('\n'),
    categories: [
      { id: 'vacuums', name: 'Vacuums', tracking: 'serial' },
      { id: 'vacuum_parts', name: 'Vacuum Parts', parent: 'parts', tracking: 'quantity' },
      { id: 'vacuum_bags_filters', name: 'Bags and Filters', parent: 'accessories', tracking: 'quantity' },
    ],
  },
};

export const INDUSTRY_IDS: IndustryId[] = ['cellphone', 'computer', 'auto', 'vacuum'];

export function isIndustryId(v: unknown): v is IndustryId {
  return typeof v === 'string' && (INDUSTRY_IDS as string[]).includes(v);
}

/** The daily close-out's words for a kind of shop (a phone shop keeps its carrier and phone sections). */
export function closeoutWords(industry: string | null | undefined) {
  const k: IndustryId = isIndustryId(industry) ? industry : 'cellphone';
  const auto = k === 'auto';
  const things = k === 'computer' ? 'COMPUTERS' : k === 'vacuum' ? 'VACUUMS' : 'PHONES';
  return {
    /** Activations, phone bills paid and phones bought: a phone shop's (others see them only with something in them). */
    phones: k === 'cellphone',
    inFor: auto ? 'VEHICLES IN FOR SERVICE' : 'CUSTOMERS IN FOR REPAIR',
    ready: auto ? 'WORK ORDERS DONE (READY)' : `${things} REPAIRED (READY)`,
    pickedUp: auto ? 'WORK ORDERS PICKED UP' : 'REPAIRS PICKED UP',
    paidToday: auto ? 'Work orders paid today' : 'Repairs paid today',
  };
}
